<template>
    <Layout :title="$t('bookEdit.title')">
        <transition name="fade" appear>
            <form v-if="localBook.id" class="book-edit__form" @submit.prevent="saveBook">
                <div class="content__title-wrap" style="justify-content: flex-start">
                    <div class="book-editable-title" :class="{ active: titleEditing }">
                        <h1 v-if="!titleEditing" class="content__title">
                            {{ localBook.name || book.name }}
                            <button
                                v-if="!titleEditing"
                                v-tooltip="$t('bookEdit.editTitleTooltip')"
                                type="button"
                                class="book-editable-title__button"
                                style="transform: translateY(-2px)"
                                @click="titleEditing = true"
                            >
                                <svg
                                    xmlns="http://www.w3.org/2000/svg"
                                    viewBox="0 0 512 512"
                                    width="14"
                                    height="14"
                                    @click.prevent
                                >
                                    <path
                                        d="M328.883 89.125l107.59 107.589-272.34 272.34L56.604 361.465l272.279-272.34zm189.23-25.948l-47.981-47.981c-18.543-18.543-48.653-18.543-67.259 0l-45.961 45.961 107.59 107.59 53.611-53.611c14.382-14.383 14.382-37.577 0-51.959zM.3 512.69c-1.958 8.812 5.998 16.708 14.811 14.565l119.891-29.069L27.473 390.597.3 512.69z"
                                    />
                                </svg>
                            </button>
                        </h1>
                        <transition name="fade">
                            <div v-if="titleEditing" class="book-editable-title__input">
                                <FormInput
                                    v-model="localBook.name"
                                    type="text"
                                    :placeholder="$t('bookEdit.bookTitle')"
                                    @enter="titleEditing = false"
                                >
                                    <template v-if="titleEditing" #append>
                                        <button
                                            v-tooltip="$t('bookEdit.saveTitleTooltip')"
                                            type="button"
                                            class="book-editable-title__button"
                                            @click="titleEditing = false"
                                        >
                                            <svg
                                                xmlns="http://www.w3.org/2000/svg"
                                                viewBox="0 0 512 512"
                                                width="14"
                                                height="14"
                                            >
                                                <path
                                                    d="M504.502 75.496c-9.997-9.998-26.205-9.998-36.204 0L161.594 382.203 43.702 264.311c-9.997-9.998-26.205-9.997-36.204 0-9.998 9.997-9.998 26.205 0 36.203l135.994 135.992c9.994 9.997 26.214 9.99 36.204 0L504.502 111.7c9.998-9.997 9.997-26.206 0-36.204z"
                                                />
                                            </svg>
                                        </button>
                                    </template>
                                </FormInput>
                            </div>
                        </transition>
                    </div>
                    <span v-if="book.symbols" class="content__post-title" style="margin-left: 20px">
                        (~{{ bookSize }})&nbsp;
                        <template v-if="book.parent">(КОПИЯ)</template>
                    </span>
                </div>
                <div class="form-group">
                    <div class="form-group__label">{{ $t('misc.pseudonym') }}</div>
                    <MultiSelect
                        v-model="author"
                        :options="authors"
                        :form-value-text="formAuthorName"
                        with-filter
                        emit-prop="id"
                        :empty-text="$t('upload.noAuthors')"
                        style="width: 300px"
                    >
                        <template #default="{ item }">
                            {{ formAuthorName(item) }}
                        </template>
                    </MultiSelect>
                </div>
                <div class="form-group">
                    <div class="form-group__label">{{ $t('bookEdit.originalTitle') }}</div>
                    <FormInput
                        v-model="localBook.original_name"
                        type="text"
                        :placeholder="$t('bookEdit.originalTitle')"
                    />
                </div>
                <div class="form-group">
                    <div class="form-group__label">{{ $t('bookEdit.ageRating.type') }}</div>
                    <CustomSelect
                        v-model="ageRatingCountry"
                        emit-prop="id"
                        view-label="name"
                        :options="ageRatingCountries"
                        width="240px"
                    />
                </div>
                <div class="form-group">
                    <div class="form-group__label">{{ $t('bookEdit.ageRating.label') }}</div>
                    <CustomSelect
                        v-model="ageRating"
                        emit-prop="id"
                        view-label="symbol"
                        :options="ageRatingsByCountry[ageRatingCountry]"
                        width="240px"
                    />
                </div>
                <div class="book-edit-cover">
                    <div class="form-group">
                        <div class="form-group__label">
                            {{ $t('bookEdit.cover') }}
                            <button
                                type="button"
                                class="book-edit__notice-link"
                                :disabled="loading"
                                @click.prevent="generateCover"
                            >
                                {{ $t('bookEdit.generate') }}
                            </button>
                        </div>
                        <div class="book-edit-cover__notice" v-html="$t('bookEdit.coverReqs')" />
                        <PhotoInput
                            class="book-edit-cover__input"
                            no-delete
                            :photo="localBook.preview_img_site"
                            @selected="onPictureSelected($event, 'preview_img')"
                            @deleted="onPictureDeleted('preview_img')"
                        />
                        <transition name="fade">
                            <span v-if="error && error['preview_img']" class="form-input__error">
                                {{ error['preview_img'][0] }}
                            </span>
                        </transition>
                    </div>
                    <div v-if="src" class="form-group">
                        <div class="form-group__label">{{ $t('bookEdit.playerLabel') }}</div>
                        <div class="player-block">
                            <Player :src="src" class="book-edit__player player--round" />
                        </div>
                    </div>
                    <div class="form-group">
                        <div class="form-group__label">{{ $t('bookEdit.document') }}</div>
                        <a :href="book.document" class="link link--attachment" download>
                            {{ docName }}
                        </a>
                    </div>
                    <div class="form-group">
                        <div class="form-group__label">
                            Разместите аудио на вашем сайте
                            <IconQuestion
                                v-tooltip="{
                                    content:
                                        'Скопируйте код виджета и установите его в том месте, <br /> в котором должен расположиться аудиоплеер для прослушивания вашей книги/статьи. <br /> Аудиофайл будет загружаться с наших серверов и не будет нагружать ваш.',
                                    html: true,
                                }"
                                fill="black"
                            />
                            <EmbedWidgetCopyButton style="margin-left: 10px" :audio="src">
                                Скопировать
                            </EmbedWidgetCopyButton>
                        </div>
                        <pre
                            style="
                                max-height: 100px;
                                width: 366px;
                                white-space: pre-wrap;
                                overflow-y: auto;
                                overflow-x: hidden;
                                background: #ffffff;
                                border: 1px solid #d8d8e1;
                                border-radius: 5px;
                                padding: 6px;
                            "
                        >
                                {{ widgetCode }}
                            </pre
                        >
                    </div>
                    <div class="form-group">
                        <div class="form-group__label">{{ $t('misc.category') }}</div>
                        <CategoryPicker
                            v-model="localBook.categories"
                            :categories="localBook.categories"
                        />
                    </div>
                    <div class="form-group">
                        <div class="form-group__label">{{ $t('bookEdit.translator') }}</div>
                        <FormInput
                            v-model="localBook.translator"
                            type="text"
                            :placeholder="$t('bookEdit.translatorPlaceholder')"
                        />
                    </div>
                    <div class="form-group">
                        <div class="form-group__label">{{ $t('bookEdit.description') }}</div>
                        <textarea
                            v-model="localBook.description"
                            class="form-group__textarea"
                            :placeholder="$t('bookEdit.descriptionPlaceholder')"
                            rows="8"
                        />
                        <div style="margin-top: 5px; margin-bottom: 5px">
                            {{ $t('misc.author') }}: {{ authorName }}
                        </div>
                        <div
                            v-if="
                                localBook.current_status.name === 'published' &&
                                localBook.publish_date
                            "
                            style="margin-bottom: 5px"
                        >
                            Дата публикации: {{ formatDdmmyyyy(localBook.publish_date) }}
                        </div>
                        <div v-if="localBook.isbnx">ISBN: {{ localBook.isbnx }}</div>
                    </div>
                    <div v-if="book.shop_link" class="form-group book-edit__publish">
                        <div class="form-group__label">Сайт вашей аудиокниги</div>
                        <div class="book-edit__shop-link">
                            <a :href="localBook.shop_link" class="link" target="_blank">
                                {{ shopLink }}
                            </a>
                        </div>
                    </div>
                    <transition name="fade" appear>
                        <button class="btn" style="position: relative" :disabled="loading">
                            {{ $t('bookEdit.save') }}
                        </button>
                    </transition>
                </div>
            </form>
        </transition>
        <Spinner v-if="loading" />
    </Layout>
</template>

<script>
import Layout from '@speechki-frontend/shared/ui/layout/Layout';
import { PlayerSimple as Player } from '@speechki-frontend/shared/ui/Player';
import {
    PhotoInput,
    FormInput,
    CustomSelect,
    MultiSelect,
    CategoryPicker,
} from '@speechki-frontend/shared/ui/forms';
import { IconQuestion } from '@speechki-frontend/shared/ui/icons';
import EmbedWidgetCopyButton from '@speechki-frontend/shared/ui/EmbedWidgetCopyButton';
import { formatDate } from '@speechki-frontend/shared/helpers/functions';

import { mapState, mapActions, mapGetters } from 'vuex';
import { generateCover, patchBook } from '../../../editor/api/books';
import { updateAuthor } from '../../../auth/api';

function getCode(audio) {
    return `<div style="display:block; width: 100%; text-align: right; padding: 3px 0;">
<audio controls style="width: 100%; outline: none;">
<source src="${audio}" type="audio/mpeg">
<span style="color:#000;">Тег audio не поддерживается вашим браузером. </span>
<a href="${audio}">Скачайте пример</a>.
</audio>
<div style="margin-top: 3px; font-size: 12px;color: #6f6f6f;">Озвучено в <a href="https://speechki.org/" target="_blank" rel="nofollow" style="color: #FF6B06; text-decoration: none;">Speechki</a></div>
</div>`;
}

export default {
    name: 'BookEdit',
    components: {
        Layout,
        Player,
        PhotoInput,
        CategoryPicker,
        FormInput,
        CustomSelect,
        IconQuestion,
        EmbedWidgetCopyButton,
        MultiSelect,
    },
    data() {
        return {
            localBook: {},
            error: null,
            loading: false,
            bookjson: '',
            titleEditing: false,
            ageRating: '',
            ageRatingCountry: 'russia',
            author: [],
        };
    },
    computed: {
        ...mapState({
            book: (state) => state.book.book,
            ageRatings: (state) => state.book.ageRatings,
            authors: (state) => state.auth.userAuthors,
        }),
        ...mapGetters(['ageRatingCountries', 'ageRatingsByCountry', 'speechkiShop']),
        bookSize() {
            const k = Math.round(this.book.symbols / 1000);
            return k + this.$t('bookEdit.size');
        },
        authorName() {
            if (!this.book.authors || !this.book.authors.length) return 'Автор неизвестен';
            return this.book.authors.reduce((prev, acc, index) => {
                return (
                    prev +
                    `${acc.first_name} ${acc.last_name}${
                        index < this.book.authors.length - 1 ? ', ' : ''
                    } `
                );
            }, '');
        },
        src() {
            if (this.localBook && this.localBook.builds && this.localBook.builds.length) {
                let mp3 = this.localBook.builds.find((build) => build.build_type === 'mp3');

                if (!mp3 || !mp3.document) return '';

                return mp3.document;
            }
            return '';
        },
        shopLink() {
            return this.localBook.shop_link.replace(/http(s?):\/\//, '');
        },
        widgetCode() {
            return getCode(this.src).trim();
        },
        docName() {
            return decodeURIComponent(this.book.document.split('/books/')[1]);
        },
    },
    watch: {
        book: {
            immediate: true,
            handler(book) {
                if (book) {
                    this.bookjson = JSON.stringify(book);
                    this.localBook = { ...book };
                    if (this.localBook.authors && this.localBook.authors.length) {
                        this.author = this.localBook.authors.map((author) => author.id);
                    }
                    if (this.localBook.age_rating) {
                        const rating = this.ageRatings.find(
                            (r) => r.id === this.localBook.age_rating.id
                        );
                        if (rating) {
                            this.ageRatingCountry = rating.country;
                        }

                        this.ageRating = this.localBook.age_rating.id;
                    }
                }
            },
        },
        ageRatingCountry(value) {
            if (this.localBook.age_rating && this.localBook.age_rating.country !== value) {
                this.ageRating = '';
            }
        },
        titleEditing(value) {
            if (!value) this.saveBook();
        },
    },
    async created() {
        this.loading = true;
        await Promise.all([
            this.fetchAgeRatings(),
            this.fetchBook(this.$route.params.id),
            this.fetchUserAuthors(),
            this.fetchCategories(),
        ]);
        this.loading = false;
    },
    beforeUnmount() {
        this.$store.commit('SET_BOOK', {});
    },
    methods: {
        ...mapActions([
            'fetchBook',
            'fetchAgeRatings',
            'fetchShopProviders',
            'fetchUserAuthors',
            'fetchCategories',
        ]),
        formatDdmmyyyy(value) {
            return formatDate(value, { pattern: 'dd.MM.yyyy' });
        },
        onPictureSelected(file, which) {
            this.localBook[which] = file;
        },
        onPictureDeleted(which) {
            this.localBook[which] = '';
        },
        async saveBook() {
            this.loading = true;
            this.error = null;
            try {
                const data = new FormData();

                const description = this.localBook.description ? this.localBook.description : '';
                const picture =
                    typeof this.localBook.preview_img !== 'string'
                        ? this.localBook.preview_img
                        : null;

                const pictureSite =
                    typeof this.localBook.preview_img_site !== 'string'
                        ? this.localBook.preview_img_site
                        : null;

                if (picture) {
                    data.append('preview_img', picture);
                }
                if (pictureSite) {
                    data.append('preview_img_site', pictureSite);
                }

                data.append('age_rating', this.ageRating);

                if (this.book.name !== this.localBook.name) {
                    data.append('name', this.localBook.name);
                }

                if (
                    this.localBook.translator !== null &&
                    typeof this.localBook.translator !== 'object'
                ) {
                    data.append('translator', this.localBook.translator);
                }

                if (
                    this.localBook.original_name !== null &&
                    typeof this.localBook.original_name !== 'object'
                ) {
                    data.append('original_name', this.localBook.original_name);
                }

                if (this.localBook.categories.length) {
                    this.localBook.categories.forEach((category) => {
                        data.append('categories', parseInt(category.id));
                    });
                }

                data.append('description', description);

                await updateAuthor(this.localBook.id, { authors: this.author });

                await patchBook(this.book.id, data);
                await this.fetchBook(this.book.id);
                this.loading = false;
            } catch (error) {
                this.loading = false;
                this.error = error;
            }
        },
        async generateCover() {
            try {
                this.loading = true;
                await generateCover(this.book.id);
                await this.fetchBook(this.book.id);
                this.loading = false;
            } catch (error) {
                this.loading = false;
                this.error = error;
            }
        },
        makeid() {
            return (
                Math.random().toString(36).substring(2, 30) +
                Math.random().toString(36).substring(2, 30)
            );
        },
        formAuthorName(author) {
            return `${author.first_name} ${author.last_name}`;
        },
    },
};
</script>

<style lang="scss" src="./BookSettingsPage.scss"></style>
